import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistProfile,
  selectIsAdmin,
  selectClinicSharesAllPatients,
} from "../../store/slices/authSlice";
import {
  setSearchQuery,
  setCurrentPage,
  toggleIncludeInactive,
  selectSearchQuery,
  selectStatusFilter,
  selectCurrentPage,
  selectIncludeInactive,
} from "../../store/slices/patientsSlice";
import { useGetPatientsQuery } from "../../store/services/patientsApi";
import PatientsTable from "../../components/patients/PatientsTable";
import PatientsCards from "../../components/patients/PatientsCards";
import { sortPatientsByName } from "../../utils/patientSortUtils";
import ViewToggle from "../../components/ui/ViewToggle";
import CustomClinikitActionButton from "../../components/ui/CustomClinikitActionButton";
import { Plus, MagnifyingGlass, UsersThree, UserCircle } from "@phosphor-icons/react";
import { HelpButton, PageHeader } from "../../components/shared";
import "./PatientsPage.css";
import { phoneMatchesQuery } from "../../utils/phoneUtils";

const VIEW_STORAGE_KEY = "clinikit_patients_view";
const MOBILE_BREAKPOINT = 768;
// Client-side pagination: fetch the full (bounded) list once, slice locally.
// Cursor-based paging was removed — it produced duplicate/skipped patients on
// page boundaries (ms-truncated createdAt cursors + dual-query merge) and made
// a true total count impossible.
const PATIENTS_PER_PAGE = 50;
const PATIENTS_FETCH_LIMIT = 1000;

function PatientsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistProfile = useSelector(selectTherapistProfile);
  const searchQuery = useSelector(selectSearchQuery);
  const statusFilter = useSelector(selectStatusFilter);
  const includeInactive = useSelector(selectIncludeInactive);
  const currentPage = useSelector(selectCurrentPage);
  const isAdmin = useSelector(selectIsAdmin);
  // Multi-clinic v2 Phase 3: "all" sharing mode — every active member sees
  // the whole clinic list (same clinic-wide query the admin pages use) with an
  // opt-in "המטופלים שלי" chip. Legacy / "assigned" clinics: unchanged.
  const sharesAllPatients = useSelector(selectClinicSharesAllPatients);
  const clinicWideList = sharesAllPatients && !isAdmin;

  // Get patient types from therapist profile
  const patientTypes = therapistProfile?.patientTypes || [];

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [onlyMine, setOnlyMine] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );

  // Initialize view from localStorage, default to 'table' (or 'cards' on mobile)
  const [viewMode, setViewMode] = useState(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      return "cards";
    }
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    return saved === "cards" ? "cards" : "table";
  });

  // Listen to window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Force cards view on mobile
      if (mobile && viewMode === "table") {
        setViewMode("cards");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  // Save view preference to localStorage
  const handleViewChange = (newView) => {
    setViewMode(newView);
    localStorage.setItem(VIEW_STORAGE_KEY, newView);
  };

  const { data, isLoading, error } = useGetPatientsQuery(
    {
      clinicId,
      therapistId: clinicWideList ? undefined : therapistId,
      status: statusFilter,
      pageSize: PATIENTS_FETCH_LIMIT,
      includeInactive,
    },
    { skip: !clinicId || !therapistId }
  );

  // Filter patients based on search (client-side, over the full list)
  const filteredPatients = useMemo(() => {
    if (!data?.patients) return [];

    let result = data.patients;

    // "המטופלים שלי" chip (shared-list mode only): primary or co-therapist
    if (clinicWideList && onlyMine) {
      result = result.filter(
        (p) =>
          p.therapistId === therapistId ||
          (Array.isArray(p.therapistIds) && p.therapistIds.includes(therapistId))
      );
    }

    // Filter by search
    if (localSearch) {
      const searchLower = localSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.firstName?.toLowerCase().includes(searchLower) ||
          p.lastName?.toLowerCase().includes(searchLower) ||
          phoneMatchesQuery(p.phone, localSearch) ||
          // Couple patients: match either partner's phone
          (p.isCouple === true &&
            (phoneMatchesQuery(p.partner1?.phone, localSearch) ||
              phoneMatchesQuery(p.partner2?.phone, localSearch)))
      );
    }

    return sortPatientsByName(result);
  }, [data?.patients, localSearch, clinicWideList, onlyMine, therapistId]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPatients.length / PATIENTS_PER_PAGE)
  );

  // Clamp current page if the list shrank (deletion, filter change, navigation)
  useEffect(() => {
    if (!isLoading && data && currentPage > totalPages) {
      dispatch(setCurrentPage(totalPages));
    }
  }, [isLoading, data, currentPage, totalPages, dispatch]);

  const pagePatients = useMemo(
    () =>
      filteredPatients.slice(
        (currentPage - 1) * PATIENTS_PER_PAGE,
        currentPage * PATIENTS_PER_PAGE
      ),
    [filteredPatients, currentPage]
  );

  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
    if (currentPage !== 1) dispatch(setCurrentPage(1));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    dispatch(setSearchQuery(localSearch));
    dispatch(setCurrentPage(1));
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page < 1 || page > totalPages) return;
      dispatch(setCurrentPage(page));
    },
    [totalPages, dispatch]
  );

  const handleAddPatient = () => {
    navigate("/dashboard/patients/new");
  };

  return (
    <div className="patients-page">
      <PageHeader
        icon={UsersThree}
        title="המטופלים שלי"
        subtitle={(() => {
          const total = data?.patients?.length || 0;
          if (!total) return "עדיין אין מטופלים";
          // Safety valve: if the clinic somehow exceeds the fetch limit,
          // signal the count is partial instead of showing a wrong number.
          const totalLabel = `${total}${data?.hasMore ? "+" : ""}`;
          if (
            (localSearch || (clinicWideList && onlyMine)) &&
            filteredPatients.length !== total
          ) {
            return `${filteredPatients.length} מתוך ${totalLabel} מטופלים`;
          }
          const inactiveCount = includeInactive
            ? data.patients.filter((p) => p.isActive === false).length
            : 0;
          const filtered = statusFilter !== "all";
          return `${totalLabel} מטופלים${
            includeInactive && inactiveCount > 0
              ? ` (${inactiveCount} לא פעילים)`
              : filtered
                ? " (מסוננים)"
                : ""
          }`;
        })()}
        actions={
          <>
            <HelpButton featureKey="patients_list" />
            <CustomClinikitActionButton
              icon={<Plus size={16} weight="bold" />}
              onClick={handleAddPatient}
            >
              מטופל חדש
            </CustomClinikitActionButton>
          </>
        }
      />

      <div className="patients-filters">
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <div className="search-input-wrapper">
            <MagnifyingGlass
              size={18}
              weight="regular"
              className="search-icon"
            />
            <input
              type="text"
              placeholder="חיפוש לפי שם או טלפון..."
              value={localSearch}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        </form>

        <div className="filters-right">
          {clinicWideList && (
            <button
              type="button"
              className={`inactive-toggle-btn mine-toggle-btn${onlyMine ? " active" : ""}`}
              aria-pressed={onlyMine}
              onClick={() => {
                setOnlyMine((prev) => !prev);
                dispatch(setCurrentPage(1));
              }}
            >
              <UserCircle size={16} weight={onlyMine ? "fill" : "regular"} />
              המטופלים שלי
            </button>
          )}
          <button
            type="button"
            className={`inactive-toggle-btn${includeInactive ? " active" : ""}`}
            onClick={() => {
              dispatch(toggleIncludeInactive());
              dispatch(setCurrentPage(1));
            }}
          >
            {includeInactive ? "הסתר מטופלים לא פעילים" : "הצג מטופלים לא פעילים"}
          </button>

          <ViewToggle
            view={viewMode}
            onViewChange={handleViewChange}
            disableTable={isMobile}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          שגיאה בטעינת מטופלים: {error.message}
        </div>
      )}

      {viewMode === "table" ? (
        <PatientsTable
          patients={pagePatients}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          patientTypes={patientTypes}
          hasFilters={!!localSearch || statusFilter !== "all" || (clinicWideList && onlyMine)}
          onAddPatient={handleAddPatient}
        />
      ) : (
        <PatientsCards
          patients={pagePatients}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          patientTypes={patientTypes}
          hasFilters={!!localSearch || statusFilter !== "all" || (clinicWideList && onlyMine)}
          onAddPatient={handleAddPatient}
        />
      )}
    </div>
  );
}

export default PatientsPage;
